/**
 * Hook: useShareCalculation
 *
 * Custom React hook for sharing calculator results via short URLs.
 * Uses GraphQL shareCalculation mutation with automatic deduplication.
 *
 * Features:
 * - Generates short shareable URLs (12-char hash)
 * - Automatic deduplication (same input → same URL)
 * - Loading and error states
 * - Works for both authenticated and guest users
 *
 * @module frontend/src/hooks/useShareCalculation
 */

import { useState } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';
import type { CalculatorFormValues } from '../../../types/calculator';

export interface ShareCalculationResult {
  calculationHash: string;
  shareUrl: string;
  isNew: boolean;
}

export interface UseShareCalculationReturn {
  shareCalculation: (
    values: CalculatorFormValues,
    year: number
  ) => Promise<ShareCalculationResult>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Custom hook for sharing calculator results
 *
 * @returns Share calculation function, loading state, error state
 *
 * @example
 * ```typescript
 * const { shareCalculation, loading, error } = useShareCalculation();
 *
 * const handleShare = async () => {
 *   try {
 *     const result = await shareCalculation(formValues, 2025);
 *     console.log('Share URL:', result.shareUrl);
 *     console.log('Is new calculation?', result.isNew);
 *   } catch (err) {
 *     console.error('Failed to share:', err);
 *   }
 * };
 * ```
 */
export function useShareCalculation(): UseShareCalculationReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Share calculation and get shareable URL
   */
  const shareCalculation = async (
    values: CalculatorFormValues,
    year: number
  ): Promise<ShareCalculationResult> => {
    setLoading(true);
    setError(null);

    try {
      const client = getAmplifyClient();

      // Normalize hourlyConfig (convert to JSON if exists)
      const hourlyConfigJson = values.hourlyConfig
        ? JSON.stringify(values.hourlyConfig)
        : null;

      // Call GraphQL mutation with API Key auth mode (works for both guest and authenticated users)
      const result = await client.mutations.shareCalculation({
        year,
        monthlyConsumption: values.monthlyConsumption,
        currentTariff: values.currentTariff,
        distributorCode: values.selectedOSD || 'TAURON', // Default to TAURON for backward compatibility
        householdSize: typeof values.householdSize === 'number' ? values.householdSize : 5,
        hasHeatPump: values.hasHeatPump,
        hasSolar: values.hasSolar,
        hasEV: values.hasEV,
        consumptionPattern: values.consumptionPattern,
        hourlyConfig: hourlyConfigJson,
      }, {
        authMode: 'apiKey', // Use API Key for guest access (works for both guest and authenticated)
      });

      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors[0].message;
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from shareCalculation mutation');
      }

      // Return successful result
      return {
        calculationHash: result.data.calculationHash,
        shareUrl: result.data.shareUrl,
        isNew: result.data.isNew,
      };

    } catch (err) {
      // Debug error information available in development if needed

      const message = err instanceof Error ? err.message : 'Failed to share calculation';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset error state
   */
  const reset = () => {
    setError(null);
  };

  return {
    shareCalculation,
    loading,
    error,
    reset,
  };
}
